import React, { useMemo } from 'react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts'
import { TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE } from '../rejections/chartTheme'
import { money, fmtShortDay } from '../financial/formatting'
import { chartRows, hasSplit } from './model'

const SEGMENTS = {
  payout: { label: 'Your payout', color: 'var(--accent)' },
  balance: { label: 'Applied to balance', color: 'var(--amber)' },
  pending: { label: 'Pending new terms', color: 'var(--text-muted)' },
}

// Lead value per day. Under an agreement the bar is stacked from the parts
// that make it up, and the parts always add back to that day's lead value -
// see chartRows() for where the remainder goes. Without one it is a single
// bar, because there is nothing to divide.
export default function DailyValueChart({ series, pendingBlock, height = 240 }) {
  const rows = useMemo(() => chartRows(series, pendingBlock), [series, pendingBlock])
  const split = (series || []).some(hasSplit)
  const keys = split
    ? ['payout', 'balance', ...(pendingBlock ? ['pending'] : [])]
    : ['payout']
  const labels = split ? SEGMENTS : { payout: { ...SEGMENTS.payout, label: 'Lead value' } }

  return (
    <section className="card" aria-label="Daily lead value" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="pd-section-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={sectionTitle}>Daily lead value</h2>
        {/* Our own legend rather than recharts': it follows the theme
            tokens, wraps on a phone, and names what each colour is. */}
        <ul aria-label="Legend" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: 0, padding: 0, listStyle: 'none' }}>
          {keys.map((k) => (
            <li key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: labels[k].color }} />
              {labels[k].label}
            </li>
          ))}
        </ul>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="date" tickFormatter={fmtShortDay} minTickGap={12}
            tick={{ fontSize: 10, fill: 'var(--chart-axis)' }} axisLine={false} tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => `$${Number(v).toLocaleString('en-US', { notation: 'compact' })}`}
            tick={{ fontSize: 10, fill: 'var(--chart-axis)' }} axisLine={false} tickLine={false} width={48}
          />
          <Tooltip
            cursor={{ fill: 'var(--chart-cursor)' }}
            contentStyle={TOOLTIP_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            labelFormatter={fmtShortDay}
            formatter={(value, name) => [money(value), labels[name]?.label || name]}
          />
          {keys.map((k, i) => (
            <Bar
              key={k} dataKey={k} name={k} stackId="value" fill={labels[k].color}
              radius={i === keys.length - 1 ? [3, 3, 0, 0] : 0}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </section>
  )
}

const sectionTitle = {
  margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.6,
  color: 'var(--text-muted)', textTransform: 'uppercase',
}
